import { updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import api from '@functions/api'

import type { PublicationBookingItemModel, PublicationOrderModel } from '@models/types'

import type {
  Action, EntityOptions, Errors, FetchRequestPayload,
} from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/publicationOrder/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/publicationOrder/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/publicationOrder/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/publicationOrder/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/publicationOrder/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/publicationOrder/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/publicationOrder/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/publicationOrder/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/publicationOrder/UPDATE_FAILURE'

export type PublicationOrderModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PublicationOrderModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function createPublicationOrder(
  publicationOrder: PublicationOrderModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      publication_order: publicationOrder,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/publication_orders.json', options, config)
      .then(({ data }: { data: PublicationOrderModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function revertPublicationOrder(
  publicationOrder: PublicationOrderModel,
  revertParams: { ticket_number: string },
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(revertParams),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_orders/${publicationOrder.id}/revert.json`, options, config)
      .then(({ data }: { data: PublicationOrderModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function sendAgencyArtworkAlert(publicationOrder: PublicationOrderModel, options: EntityOptions = {}) {
  const config = { method: 'POST' }

  return () => {
    const promise = api(`/publication_orders/${publicationOrder.id}/send_agency_artwork_alert.json`, options, config)
      .then(({ data }: { data: { queued: object } }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPublicToken(publicationOrder: PublicationOrderModel, options: EntityOptions = {}) {
  const config = { method: 'PUT' }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_orders/${publicationOrder.id}/create_public_token.json`, options, config)
      .then(({ data }: { data: PublicationOrderModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function autoPaginate(
  publicationEditionId: number,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      edition_id: publicationEditionId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/publication_orders/auto_paginate.json', options, config)
      .then(({ data }: { data: PublicationOrderModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function removedBookingItems(publicationOrderId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/publication_orders/${publicationOrderId}/removed_booking_items.json`, options)
      .then(({ data }: { data: PublicationBookingItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function sendAgencyBookingAlert(publicationOrder: PublicationOrderModel, options: EntityOptions = {}) {
  const config = { method: 'POST' }

  return () => {
    const promise = api(`/publication_orders/${publicationOrder.id}/send_agency_booking_alert.json`, options, config)
      .then(({ data }: { data: Partial<PublicationOrderModel> }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkReleasePublicationBookings(
  publicationOrder: Partial<PublicationOrderModel>,
  options?: EntityOptions,
) {
  const config = { method: 'PUT' }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/publication_orders/${publicationOrder.id}/bulk_release.json`, options, config)
      .then(({ data }: { data: PublicationOrderModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: PublicationOrderModuleState = initialState,
  action: Action = { type: '' },
): PublicationOrderModuleState {
  switch (action.type){
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
